import GetAnalyticAccountsBalanceSheetDto from 'backend2/dtos/accounting/Accounts/GetAnalyticAccountsBalanceSheetDto';
import GetSyntheticAccountsBalanceSheetDto from 'backend2/dtos/accounting/Accounts/GetSyntheticAccountsBalanceSheetDto';
import GetOperations from 'backend2/dtos/accounting/Operations/GetOperations.dto';
import GetOperationsGroupingDateDto from 'backend2/dtos/accounting/Operations/grouping/GetOperationsGroupingDate.dto';
import GetOperationsGroupingTypeDto from 'backend2/dtos/accounting/Operations/grouping/GetOperationsGroupingType.dto';
import FullOperation, { Payment } from 'backend2/dtos/accounting/Operations/Operation.dto';
import GetHistory from 'backend2/dtos/common/History/GetHistory.dto';
import { IScheme } from 'backend2/interfaces/accounts';
import { ICount } from 'backend2/interfaces/common';
import { IFullHistory } from 'backend2/interfaces/history';
import { IFullOperation, IOperationsGroupingDate, IOperationsGroupingType } from 'backend2/interfaces/operations';
import { DeepPartial } from 'backend2/types';
import { AnalyticBalanceSheet, SyntheticBalanceSheet } from 'backend2/types/accounts';
import isArray from 'lodash-es/isArray';
import isObject from 'lodash-es/isObject';
import { makeAutoObservable, runInAction } from 'mobx';
import {
	approveOperation,
	canIChangeApproveStateOperation,
	canIRemoveOperation,
	canIUpdateOperation,
	createOperation,
	fetchAccountsScheme,
	fetchAnalyticAccountsBalanceSheet,
	fetchOperations,
	fetchOperationsGroupingDate,
	fetchOperationsGroupingType,
	fetchOperationsHistory,
	fetchSyntheticAccountsBalanceSheet,
	removeOperation,
	unapproveOperation,
	updateOperation,
	updateOperationPayments,
} from 'src/reduxStore/dataProviders/accounting';

export const initialAccountingScheme: IScheme = {
	accounts: [],
	childrenAccountIdsByAccountId: {},
	deepChildrenAccountIdsByAccountId: {},
	readAccessLeafAccountIds: [],
	accountsUserTypeCorrespondence: {},
	operations: [],
	manualOperationAccountsCorrespondenceMap: {},
	accountsGroups: [],
	frontendOperationsGroups: [],
	vatPercentage: 0,
};

class ObservableAccountingStore {
	scheme: IScheme = initialAccountingScheme;
	syntheticAccountsBalanceSheet: SyntheticBalanceSheet = {};
	analyticAccountsBalanceSheet: AnalyticBalanceSheet = [] as AnalyticBalanceSheet;
	hotAnalyticAccountsBalanceSheet: AnalyticBalanceSheet = [] as AnalyticBalanceSheet;
	operations: Record<string, IFullOperation> = {};
	hotOperationIds: string[] = [];
	operationsHistory: IFullHistory[] = [];
	hotOperationsHistory: IFullHistory[] = [];
	operationsGroupingDate: IOperationsGroupingDate[] = [];
	operationsGroupingType: IOperationsGroupingType[] = [];

	constructor() {
		makeAutoObservable(this, null, { autoBind: true });
	}

	get allOperations() {
		return Object.values(this.operations);
	}

	get hotOperations() {
		return this.hotOperationIds.map((operationId) => this.operations[operationId]).filter(Boolean);
	}

	getOperation(operationId: string | bigint) {
		return this.operations[String(operationId)];
	}

	getOperationHistory(operationId: bigint | string) {
		return this.operationsHistory.filter(({ fieldEntityId }) => String(fieldEntityId) === String(operationId));
	}

	setScheme(scheme: IScheme) {
		this.scheme = scheme;
	}

	setSyntheticAccountsBalanceSheet(syntheticAccountsBalanceSheet: SyntheticBalanceSheet) {
		this.syntheticAccountsBalanceSheet = syntheticAccountsBalanceSheet;
	}

	mergeAnalyticAccountsBalanceSheet(analyticAccountsBalanceSheet: AnalyticBalanceSheet) {
		this.analyticAccountsBalanceSheet = {
			...this.analyticAccountsBalanceSheet,
			...analyticAccountsBalanceSheet,
		} as AnalyticBalanceSheet;
	}

	setHotAnalyticAccountsBalanceSheet(analyticAccountsBalanceSheet: AnalyticBalanceSheet) {
		this.hotAnalyticAccountsBalanceSheet = analyticAccountsBalanceSheet;
	}

	resetHotAnalyticAccountsBalanceSheet() {
		this.hotAnalyticAccountsBalanceSheet = [] as AnalyticBalanceSheet;
	}

	mergeOperations(operations: IFullOperation[]) {
		for (const fullOperation of operations) {
			this.operations[String(fullOperation.operation.id)] = fullOperation;
		}
	}

	setHotOperations(operations: IFullOperation[]) {
		this.mergeOperations(operations);
		this.hotOperationIds = operations.map((fullOperation) => String(fullOperation.operation.id));
	}

	removeOperationFromStore(operationId: bigint | string) {
		delete this.operations[String(operationId)];
		this.hotOperationIds = this.hotOperationIds.filter((hotOperationId) => hotOperationId !== String(operationId));
	}

	addOperationsHistory(operationsHistory: IFullHistory[]) {
		const operationsHistoryById = new Map<string, IFullHistory>();
		for (const fullHistory of this.operationsHistory) {
			operationsHistoryById.set(constructOperationHistoryId(fullHistory), fullHistory);
		}
		for (const fullHistory of operationsHistory) {
			operationsHistoryById.set(constructOperationHistoryId(fullHistory), fullHistory);
		}
		this.operationsHistory = Array.from(operationsHistoryById.values());
	}

	setHotOperationsHistory(operationsHistory: IFullHistory[]) {
		this.hotOperationsHistory = operationsHistory;
	}

	setOperationsGroupingDate(operationsGroupingDate: IOperationsGroupingDate[]) {
		this.operationsGroupingDate = operationsGroupingDate;
	}

	setOperationsGroupingType(operationsGroupingType: IOperationsGroupingType[]) {
		this.operationsGroupingType = operationsGroupingType;
	}

	clear() {
		this.scheme = initialAccountingScheme;
		this.syntheticAccountsBalanceSheet = {};
		this.analyticAccountsBalanceSheet = [] as AnalyticBalanceSheet;
		this.hotAnalyticAccountsBalanceSheet = [] as AnalyticBalanceSheet;
		this.operations = {};
		this.hotOperationIds = [];
		this.operationsHistory = [];
		this.hotOperationsHistory = [];
		this.operationsGroupingDate = [];
		this.operationsGroupingType = [];
	}

	async fetchAccountsScheme() {
		const scheme = await fetchAccountsScheme();
		runInAction(() => {
			this.setScheme(scheme);
		});
		return scheme;
	}

	async fetchSyntheticAccountsBalanceSheet(
		canTakeFromLocalStorage: boolean,
		accountId: number,
		params?: GetSyntheticAccountsBalanceSheetDto,
		save = true,
	) {
		if (canTakeFromLocalStorage) {
			// todo: если схема не поменялась
		}

		const syntheticAccountsBalanceSheet = await fetchSyntheticAccountsBalanceSheet(accountId, params);

		if (!isObject(syntheticAccountsBalanceSheet)) {
			return {};
		}

		if (save) {
			runInAction(() => {
				this.setSyntheticAccountsBalanceSheet(syntheticAccountsBalanceSheet);
			});
		}

		return syntheticAccountsBalanceSheet;
	}

	async fetchAnalyticAccountsBalanceSheet(isHot: boolean, accountId: number, params?: GetAnalyticAccountsBalanceSheetDto) {
		const analyticAccountsBalanceSheet = await fetchAnalyticAccountsBalanceSheet(accountId, params);

		if (!isObject(analyticAccountsBalanceSheet)) {
			return {};
		}

		runInAction(() => {
			this.mergeAnalyticAccountsBalanceSheet(analyticAccountsBalanceSheet);
			if (isHot) {
				this.setHotAnalyticAccountsBalanceSheet(analyticAccountsBalanceSheet);
			}
		});

		return analyticAccountsBalanceSheet;
	}

	async createOperation(body: DeepPartial<FullOperation>) {
		return createOperation(body);
	}

	async updateOperation(body: DeepPartial<FullOperation>, operationId: bigint) {
		const operation = await updateOperation(body, operationId);

		if (!isObject(operation)) {
			return {};
		}

		runInAction(() => {
			this.mergeOperations([operation]);
		});

		return operation;
	}

	async fetchOperations(isHot: boolean, params?: GetOperations) {
		const operations = await fetchOperations(params);

		if (!isArray(operations)) {
			return [];
		}

		runInAction(() => {
			if (isHot) {
				this.setHotOperations(operations);
			} else {
				this.mergeOperations(operations);
			}
		});

		return operations;
	}

	async fetchOperationsCount(params: GetOperations = {}) {
		const count = await fetchOperations({
			...params,
			isCountMode: true,
		});

		// @ts-ignore
		count.count = BigInt(count.count);
		return count as unknown as ICount;
	}

	async fetchAnalyticAccountsBalanceSheetCount(accountId: number, params: GetAnalyticAccountsBalanceSheetDto = {}) {
		const count = await fetchAnalyticAccountsBalanceSheet(accountId, {
			...params,
			isCountMode: true,
		});

		// @ts-ignore
		count.count = BigInt(count.count);
		return count as unknown as ICount;
	}

	async removeOperation(operationId: bigint) {
		const operation = await removeOperation(operationId);

		runInAction(() => {
			this.removeOperationFromStore(operationId);
		});

		return operation;
	}

	async approveOperation(operationId: bigint) {
		const operation = await approveOperation(operationId);

		if (!isObject(operation)) {
			return {};
		}

		runInAction(() => {
			this.mergeOperations([operation]);
		});

		return operation;
	}

	async unapproveOperation(operationId: bigint) {
		const operation = await unapproveOperation(operationId);

		if (!isObject(operation)) {
			return {};
		}

		runInAction(() => {
			this.mergeOperations([operation]);
		});

		return operation;
	}

	async updateOperationPayments(body: Pick<Payment, 'accountId' | 'amount'>[], operationId: bigint) {
		const operation = await updateOperationPayments(body, operationId);

		if (!isObject(operation)) {
			return {};
		}

		runInAction(() => {
			this.mergeOperations([operation]);
		});

		return operation;
	}

	async fetchOperationsGroupingDate(params?: GetOperationsGroupingDateDto) {
		const operationsGroupingDate = await fetchOperationsGroupingDate(params);

		if (!isArray(operationsGroupingDate)) {
			return [];
		}

		runInAction(() => {
			this.setOperationsGroupingDate(operationsGroupingDate);
		});

		return operationsGroupingDate;
	}

	async fetchOperationsGroupingDateCount(params: GetOperationsGroupingDateDto = {}) {
		const count = await fetchOperationsGroupingDate({
			...params,
			isCountMode: true,
		});

		// @ts-ignore
		count.count = BigInt(count.count);
		return count as unknown as ICount;
	}

	async fetchOperationsGroupingType(params?: GetOperationsGroupingTypeDto) {
		const operationsGroupingType = await fetchOperationsGroupingType(params);

		if (!isArray(operationsGroupingType)) {
			return [];
		}

		runInAction(() => {
			this.setOperationsGroupingType(operationsGroupingType);
		});

		return operationsGroupingType;
	}

	async fetchOperationsGroupingTypeCount(params: GetOperationsGroupingTypeDto) {
		const count = await fetchOperationsGroupingType({
			...params,
			isCountMode: true,
		});

		// @ts-ignore
		count.count = BigInt(count.count);
		return count as unknown as ICount;
	}

	async getCanIUpdateOperation(operationId: bigint | string) {
		try {
			await canIUpdateOperation(operationId);
		} catch (ex) {
			if (ex?.response?.status === 403) {
				return false;
			}
			throw ex;
		}
		return true;
	}

	async getIChangeApproveStateOperation(operationId: bigint | string) {
		try {
			await canIChangeApproveStateOperation(operationId);
		} catch (ex) {
			if (ex?.response?.status === 403) {
				return false;
			}
			throw ex;
		}
		return true;
	}

	async getCanIRemoveOperation(operationId: bigint | string) {
		try {
			await canIRemoveOperation(operationId);
		} catch (ex) {
			if (ex?.response?.status === 403) {
				return false;
			}
			throw ex;
		}
		return true;
	}

	async fetchOperationsHistory(isHot: boolean, params: GetHistory = {}) {
		const operationsHistory = await fetchOperationsHistory(params);

		if (!operationsHistory) {
			return [];
		}

		runInAction(() => {
			this.addOperationsHistory(operationsHistory);
			if (isHot) {
				this.setHotOperationsHistory(operationsHistory);
			}
		});

		return operationsHistory;
	}

	async fetchOperationsHistoryCount(params: GetHistory = {}) {
		const count = await fetchOperationsHistory({
			...params,
			isCountMode: true,
		});

		// @ts-ignore
		count.count = BigInt(count.count);
		return count as unknown as ICount;
	}
}

function constructOperationHistoryId(fullHistory: IFullHistory) {
	return `${fullHistory.actionId}__${fullHistory.historyFieldId}`;
}

const AccountingStore = new ObservableAccountingStore();
export default AccountingStore;
